import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { CssValue } from '../type/cssTemplate';

/**
 * @author pjh
 * @date 2025.09.23
 * @description CSS 요소 관리
 */

type CssValueTemplateStore = {
  values: CssValue[] | null;
  loading: boolean;
  getCssValues: (productId: string) => Promise<CssValue[]>;
  // 일반 사용자: 선택한 CSS 콘텐츠 적용
  useCssContent: (productId: string, cssContentIds: string[]) => Promise<void>;
  // 생성 API (카테고리만 생성 시 contents 빈 배열 허용)
  createCategory: (categoryName: string, originCssName: string) => Promise<void>;
  createContent: (
    payload: {
      categoryName: string;
      originCssName: string;
      contents: Array<{
        cssContentId?: string;
        cssName: string;
        cssValue: string | null;
        uploadFileId?: any; // UploadFileDto.Response 형태
        isUsed?: boolean;
        name?: string;
      }>
    }
  ) => Promise<void>;
  // 보류 상태 및 조작
  pendingCreates: Array<{
    categoryName: string;
    originCssName: string;
    contents: Array<{
      cssContentId?: string;
      cssName: string;
      cssValue: string | null;
      uploadFileId?: string; // id only
      isUsed?: boolean;
      previewUrl?: string; // UI only, not sent to server
      name?: string;
    }>;
  }>;
  addPendingCategory: (categoryName: string, originCssName: string) => void;
  setPendingOrigin: (categoryName: string, originCssName: string) => void;
  addPendingItem: (
    categoryName: string,
    item: { cssContentId?: string; cssName: string; cssValue: string | null; uploadFileId?: string; isUsed?: boolean; previewUrl?: string; name?: string },
    meta?: { previousKey?: string }
  ) => void;
  clearPending: () => void;
  savePending: () => Promise<void>;
};

export const useCssValueTemplateStore = create<CssValueTemplateStore>((set) => ({
  values: null,
  loading: false,
  pendingCreates: [],

  getCssValues: async (productId: string) => {
    set({ loading: true });
    try {
      const res = await apiClient.get(`/css/content/${productId}`);
      const data = (res.data || []) as CssValue[];
      set({ values: data, loading: false });
      return data;
    } catch (err) {
      console.error('CSS 값 템플릿 조회 실패:', err);
      set({ loading: false });
      throw err;
    }
  },

  // 일반 사용자: 카테고리별 선택 항목을 상품에 적용
  useCssContent: async (productId: string, cssContentIds: string[]) => {
    try {
      const params = new URLSearchParams();
      (cssContentIds || []).forEach(id => params.append('cssContentIds', id));
      await apiClient.patch(`/css/content/${productId}?${params.toString()}`, {});
    } catch (err) {
      console.error('선택한 CSS 콘텐츠 적용 실패:', err);
      throw err;
    }
  },

  // 카테고리 생성 (어드민) - contents 없이 생성
  createCategory: async (categoryName: string, originCssName: string) => {
    try {
      await apiClient.post(`/css/content`, { categoryName, originCssName, contents: [] });
    } catch (err) {
      console.error('카테고리 생성 실패:', err);
      throw err;
    }
  },

  // 항목 생성 (어드민)
  // 항목 생성 (어드민) - ContentCreate 규격으로 단건 또는 복수 contents 전송
  createContent: async (payload) => {
    try {
      await apiClient.post(`/css/content`, payload);
    } catch (err) {
      console.error('CSS 콘텐츠 생성 실패:', err);
      throw err;
    }
  },

  // 보류 로컬 상태 조작
  addPendingCategory: (categoryName, originCssName) => {
    (set as any)((state: CssValueTemplateStore) => {
      const exists = state.pendingCreates.some(c => c.categoryName === categoryName);
      if (exists) {
        return {
          pendingCreates: state.pendingCreates.map(c => c.categoryName === categoryName ? { ...c, originCssName } : c)
        };
      }
      return { pendingCreates: [...state.pendingCreates, { categoryName, originCssName, contents: [] }] };
    });
  },
  setPendingOrigin: (categoryName, originCssName) => {
    (set as any)((state: CssValueTemplateStore) => ({
      pendingCreates: state.pendingCreates.map(c => c.categoryName === categoryName ? { ...c, originCssName } : c)
    }));
  },
  addPendingItem: (categoryName, item, meta) => {
    (set as any)((state: CssValueTemplateStore) => {
      const pending = [...state.pendingCreates];
      const idx = pending.findIndex(c => c.categoryName === categoryName);
      const applyUpdate = (contents: any[] = []) => {
        const next = [...contents];
        const previousKey = meta?.previousKey?.trim() || '';
        const matchKeys = [
          item.cssContentId?.trim() || undefined,
          previousKey && previousKey !== item.cssContentId ? previousKey : undefined,
          !item.cssContentId ? item.cssName : undefined,
          item.cssContentId ? item.cssName : undefined
        ].filter((v): v is string => !!v && v.trim().length > 0);

        const matchesKey = (content: any, key: string) => {
          if (!key) return false;
          if (typeof content.cssContentId === 'string' && content.cssContentId.trim() === key) {
            return true;
          }
          if ((!content.cssContentId || content.cssContentId === '') && typeof content.cssName === 'string' && content.cssName.trim() === key) {
            return true;
          }
          return false;
        };

        const existingIdx = next.findIndex((content) => matchKeys.some((key) => matchesKey(content, key)));
        if (existingIdx >= 0) {
          next[existingIdx] = { ...next[existingIdx], ...item };
        } else {
          next.push(item);
        }
        return next;
      };

      if (idx === -1) {
        pending.push({ categoryName, originCssName: '', contents: applyUpdate([]) });
      } else {
        pending[idx] = { ...pending[idx], contents: applyUpdate(pending[idx].contents || []) };
      }
      return { pendingCreates: pending };
    });
  },
  clearPending: () => set({ pendingCreates: [] }),
  savePending: async () => {
    const state = ( (set as any).getState?.() || useCssValueTemplateStore.getState() ) as CssValueTemplateStore;
    const list = state.pendingCreates || [];
    if (list.length === 0) return;
    // sanitize payload: strip previewUrl and ensure uploadFileId is string
    const payload = list.map(cat => ({
      categoryName: cat.categoryName,
      originCssName: cat.originCssName,
      contents: (cat.contents || []).map(c => ({
        cssContentId: c.cssContentId ? c.cssContentId : undefined,
        cssName: c.cssName,
        cssValue: c.cssValue ?? null,
        uploadFileId: c.uploadFileId,
        isUsed: !!c.isUsed,
        name: c.name,
      }))
    }));
    await apiClient.post('/css/content', payload);
    set({ pendingCreates: [] });
  },
}));
